"use client"

import { DRIVERS_HUB_COPY } from "@/constants/drivers/trust-copy"
import Head from "next/head"
import { DriverProfile } from "@/lib/drivers/types"
import { DriverProfileClient } from "@/components/drivers/driver-profile-client"
import PageNav from "@/storyblok/sections/PageNav"
import Footer from "@/storyblok/static/Footer"
import LocalPageHeader from "@/storyblok/sections/LocalPageHeader"
import {
  driverDescription,
  driverMetaDescription,
  mostCommonCategory,
} from "@/util/drivers/utils"
import { getDriverProfileRichSnippets } from "@/app/seo/driver-profile-rich-snippets"
import { toJsonLdString } from "@/seo/jsonld"

interface DriverProfileContentProps {
  profile: DriverProfile
  /** Serialized at ISR time via getStaticProps — do not recompute at render. */
  tenureLabel: string | null
  tenureSentence: string
}

const CTA_LABEL = "Get Transportation Quotes"

export function DriverProfileContent({
  profile,
  tenureLabel,
  tenureSentence,
}: DriverProfileContentProps) {
  const { data } = profile
  const title = `${data.displayName} - Professional Transporter | CitizenShipper`
  const description = driverMetaDescription(data, tenureSentence)

  const breadcrumbs = [
    { name: "Home", href: "/" },
    { name: DRIVERS_HUB_COPY.pet.headingNoun, href: "/drivers" },
    { name: data.displayName, href: `/drivers/${data.id}` },
  ]

  // Determine the CTA URL based on driver's primary shipment category
  const primaryCategory = data.recentTrips
    ? mostCommonCategory(data.recentTrips)
    : undefined
  const isPetDriver =
    primaryCategory && primaryCategory.toLowerCase().includes("pet")

  // For the hero form, use relative path
  const heroFormPath = isPetDriver ? "/shipments/new/animals" : "shipments/new"

  // For QuoteCTA and external links, use full URL
  const ctaUrl = isPetDriver
    ? "https://app.citizenshipper.com/shipments/new/animals"
    : "https://app.citizenshipper.com/shipments/new"

  const headerProps = {
    heading: data.displayName,
    subheading: `Professional Transporter${
      tenureLabel ? ` · CitizenShipper Member for ${tenureLabel}` : ""
    }`,
    description: driverDescription(data),
    hasLongDescription: true,
    buttonLabel: CTA_LABEL,
    buttonLink: heroFormPath,
    image: {
      alt: data.displayName,
      filename:
        data.profilePhoto?.large?.replace("/m/", "") ||
        "https://a-us.storyblok.com/f/1002522/716x860/f51afecb18/header-hero.png",
    },
    smBgImage: {
      alt: data.displayName,
      filename:
        data.profilePhoto?.small?.replace("/m/", "") ||
        "https://a-us.storyblok.com/f/1002522/375x714/1ab232cc02/header-hero-sm.png",
    },
  }

  const canonicalUrl = `${
    process.env.NEXT_PUBLIC_WEBSITE_HOST || "https://citizenshipper.com"
  }/drivers/${data.id}`

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="CitizenShipper" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {data.profilePhoto?.large && (
          <>
            <meta
              property="og:image"
              content={data.profilePhoto.large.replace("/m/", "")}
            />
            <meta property="og:image:alt" content={data.displayName} />
          </>
        )}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:site" content="@CitizenShipper" />
        {data.profilePhoto?.large && (
          <>
            <meta
              name="twitter:image"
              content={data.profilePhoto.large.replace("/m/", "")}
            />
            <meta name="twitter:image:alt" content={data.displayName} />
          </>
        )}
        <link rel="canonical" href={canonicalUrl} />
        {getDriverProfileRichSnippets(data).map((snippet, index) => (
          <script
            key={`driver-schema-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: toJsonLdString(snippet) }}
          />
        ))}
      </Head>
      <PageNav
        noBlok
        buttonLabel={CTA_LABEL}
        {...(isPetDriver
          ? { externalPath: ctaUrl }
          : { route: "shipments/new" })}
      />
      <LocalPageHeader {...headerProps} items={breadcrumbs} />
      <div className="pt-8 lg:pt-16">
        <DriverProfileClient
          profile={profile}
          ctaUrl={ctaUrl}
          ctaLabel={CTA_LABEL}
        />
      </div>
      <Footer noBlok />
    </>
  )
}
